import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Card,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { serieService, unidadeService } from '../services/api';

const SeriesList = () => {
  const [series, setSeries] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  const fetchSeries = async () => {
    setLoading(true);
    try {
      const response = await serieService.list();
      setSeries(response.data);
    } catch (error) {
      message.error('Erro ao carregar séries');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      message.error('Erro ao carregar unidades');
    }
  };

  useEffect(() => {
    fetchSeries();
    fetchUnidades();
  }, []);

  const getUnidadeName = (id_unidades) => {
    const unidade = unidades.find((u) => u.id === id_unidades);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const handleCreate = () => {
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      id_unidades: record.id_unidades,
      nome_serie: record.nome_serie,
    });
    setIsModalVisible(true);
  };

  const handleSave = async (values) => {
    try {
      if (editingId) {
        await serieService.update(editingId, values);
        message.success('Série atualizada com sucesso');
      } else {
        await serieService.create(values);
        message.success('Série criada com sucesso');
      }
      setIsModalVisible(false);
      form.resetFields();
      fetchSeries();
    } catch (error) {
      const errorMsg = error.response?.data?.detail
        ? (typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao salvar série';
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await serieService.delete(id);
      message.success('Série deletada com sucesso');
      fetchSeries();
    } catch (error) {
      message.error('Erro ao deletar série');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0) },
    {
      title: 'Unidade',
      dataIndex: 'id_unidades',
      key: 'id_unidades',
      render: (id_unidades) => getUnidadeName(id_unidades),
      sorter: (a, b) => compareText(getUnidadeName(a.id_unidades), getUnidadeName(b.id_unidades)),
    },
    {
      title: 'Nome da Série',
      dataIndex: 'nome_serie',
      key: 'nome_serie',
      sorter: (a, b) => compareText(a.nome_serie, b.nome_serie),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Editar
          </Button>
          <Popconfirm
            title="Confirmar exclusão"
            description="Tem certeza que deseja deletar esta série?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">
              Deletar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Séries"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Novo
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchSeries} loading={loading}>
              Atualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={series}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Editar Série' : 'Nova Série'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        okText={editingId ? 'Atualizar' : 'Criar'}
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="id_unidades"
            label="Unidade"
            rules={[{ required: true, message: 'Selecione a unidade' }]}
          >
            <Select placeholder="Selecione a unidade" showSearch optionFilterProp="children">
              {unidades.map((unidade) => (
                <Select.Option key={unidade.id} value={unidade.id}>
                  {unidade.nome_fantasia}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="nome_serie"
            label="Nome da Série"
            rules={[{ required: true, message: 'Informe o nome da série' }]}
          >
            <Input placeholder="Ex: 1º Ano, 2º Ano" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SeriesList;
